import { FC, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import Link from 'next/link';

import { ProgramData, ProgramSchedule } from 'components/SessionsCalendar/types';

import imageUrlGenerator from 'utils/imageUrlGenerator';
import { updateProgramSchedule } from 'actions/programs';

import Text from 'components/core/Text';

import { Card, AddCard, TextWrapper, Icon, DeleteButton } from './styles';

interface Props {
  day?: string;
  program?: ProgramData;
  schedule?: ProgramSchedule;
  onClick?: () => void;
}

const defaultProgram: ProgramData = {
  id: 0,
  name: '',
  picture: '',
};

const CalendarCard: FC<Props> = ({ program, schedule, day, onClick }) => {
  const { picture, name, id } = useMemo(() => program || defaultProgram, [program]);
  const programLink = useMemo(() => `/programs/${id}`, [id]);
  const dispatch = useDispatch();

  const handleDelete = useCallback(
    (e) => {
      e.preventDefault();

      if (!program || !schedule || !day) {
        return;
      }

      dispatch(
        updateProgramSchedule({
          schedule: { ...schedule, [day]: false },
          programId: id,
        }),
      );
    },
    [day, dispatch, id, program, schedule],
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick?.();
      }
    },
    [onClick],
  );

  if (!program) {
    return (
      <AddCard
        onClick={onClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-label="Add session"
      >
        <Icon>+</Icon>
      </AddCard>
    );
  }

  return (
    <Link href={programLink}>
      <a href={programLink}>
        <Card onClick={onClick} src={imageUrlGenerator(picture, '170x100')}>
          <TextWrapper>
            <Text color="white" transform="uppercase" withEllipsis withShadow>
              {name}
            </Text>
          </TextWrapper>
          <DeleteButton onClick={handleDelete} aria-label="Remove session" />
        </Card>
      </a>
    </Link>
  );
};

export default CalendarCard;
